import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Clock, Send, Settings, Plus, Edit, Trash2, Eye, Calendar, Ban, UserX, History, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EmailManagement = () => {
  const [activeTab, setActiveTab] = useState('logs'); // 'logs', 'templates', 'scheduler', 'optouts'
  const [user, setUser] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [optouts, setOptouts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showOptoutModal, setShowOptoutModal] = useState(false);
  const [newOptoutEmail, setNewOptoutEmail] = useState('');
  const [newOptoutName, setNewOptoutName] = useState('');
  const [newOptoutReason, setNewOptoutReason] = useState('');

  // Filters
  const [logFilters, setLogFilters] = useState({
    email_type: '',
    status: '',
    limit: 50
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('rooftrack_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('rooftrack_user') || '{}');
    setUser(userData);
    // Use user.id (UUID) instead of email for API calls
    const userId = userData?.id || userData?.email;
    if (userId) {
      loadEmailLogs(userId);
      loadTemplates(userId);
      loadSchedulerConfig(userId);
      loadOptouts(userId);
    }
  }, []);

  const getUserId = () => user?.id || user?.email;

  const loadEmailLogs = async (userId) => {
    try {
      const params = { user_id: userId, ...logFilters };
      const response = await axios.get(`${BACKEND_URL}/api/emails/logs`, {
        params,
        headers: getAuthHeaders()
      });
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading email logs:', error);
    }
  };

  const loadOptouts = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/emails/optouts`, {
        params: { user_id: userId },
        headers: getAuthHeaders()
      });
      setOptouts(response.data.optouts || []);
    } catch (error) {
      console.error('Error loading optouts:', error);
    }
  };

  const handleAddOptout = async () => {
    if (!newOptoutEmail) {
      alert('Please enter an email address');
      return;
    }
    
    try {
      setIsLoading(true);
      await axios.post(
        `${BACKEND_URL}/api/emails/optouts`,
        {},
        {
          params: { 
            user_id: getUserId(), 
            email: newOptoutEmail,
            name: newOptoutName || undefined,
            reason: newOptoutReason || 'Manual opt-out'
          },
          headers: getAuthHeaders()
        }
      );
      alert(`Stopped all emails to ${newOptoutEmail}`);
      setNewOptoutEmail('');
      setNewOptoutName('');
      setNewOptoutReason('');
      setShowOptoutModal(false);
      loadOptouts(getUserId());
    } catch (error) {
      alert('Failed to add opt-out: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveOptout = async (optoutId, email) => {
    if (!window.confirm(`Re-enable emails to ${email}?`)) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/emails/optouts/${optoutId}`,
        {
          params: { user_id: getUserId() },
          headers: getAuthHeaders()
        }
      );
      alert('Emails re-enabled for this address');
      loadOptouts(getUserId());
    } catch (error) {
      alert('Failed to remove opt-out: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStopEmailsFromLog = async (email, name) => {
    if (!window.confirm(`Stop all future emails to ${email}?`)) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/emails/optouts`,
        {},
        {
          params: { 
            user_id: getUserId(), 
            email: email,
            name: name || undefined,
            reason: 'Stopped from email logs'
          },
          headers: getAuthHeaders()
        }
      );
      alert(`Stopped all emails to ${email}`);
      loadOptouts(getUserId());
      loadEmailLogs(getUserId());
    } catch (error) {
      alert('Failed to stop emails: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadTemplates = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/emails/templates`, {
        params: { user_id: userId },
        headers: getAuthHeaders()
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadSchedulerConfig = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/emails/scheduler/config`, {
        params: { user_id: userId },
        headers: getAuthHeaders()
      });
      setSchedulerConfig(response.data);
    } catch (error) {
      console.error('Error loading scheduler config:', error);
    }
  };

  const initializeTemplates = async () => {
    try {
      setIsLoading(true);
      await axios.post(
        `${BACKEND_URL}/api/emails/templates/initialize`,
        {},
        {
          params: { user_id: getUserId() },
          headers: getAuthHeaders()
        }
      );
      alert('Default templates created successfully!');
      loadTemplates(getUserId());
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to initialize templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSchedulerConfig = async () => {
    try {
      setIsLoading(true);
      await axios.put(
        `${BACKEND_URL}/api/emails/scheduler/config`,
        schedulerConfig,
        { headers: getAuthHeaders() }
      );
      alert('Scheduler configuration saved successfully!');
    } catch (error) {
      alert('Failed to save scheduler configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTemplate = async (template) => {
    try {
      setIsLoading(true);
      if (template.id) {
        // Update existing
        await axios.put(
          `${BACKEND_URL}/api/emails/templates/${template.id}`,
          template,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new
        await axios.post(
          `${BACKEND_URL}/api/emails/templates`,
          { ...template, user_id: getUserId() },
          { headers: getAuthHeaders() }
        );
      }
      alert('Template saved successfully!');
      loadTemplates(getUserId());
      setShowTemplateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      alert('Failed to save template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      console.log('Deleting template:', templateId);
      console.log('User ID:', getUserId());
      
      const response = await axios.delete(
        `${BACKEND_URL}/api/emails/templates/${templateId}`,
        {
          params: { user_id: getUserId() },
          headers: getAuthHeaders()
        }
      );
      
      console.log('Delete response:', response.data);
      alert('Template deleted successfully!');
      loadTemplates(getUserId());
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Failed to delete template: ${error.response?.data?.detail || error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Management</h1>
        <p className="text-gray-600">Manage email automation, templates, and track sent emails</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('logs')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logs'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail className="inline-block w-5 h-5 mr-2" />
            Email Logs
          </button>
          <button
            onClick={() => setActiveTab('optouts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'optouts'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Ban className="inline-block w-5 h-5 mr-2" />
            Stopped Emails
            {optouts.length > 0 && (
              <span className="ml-2 bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs">
                {optouts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Edit className="inline-block w-5 h-5 mr-2" />
            Email Templates
          </button>
          <button
            onClick={() => setActiveTab('scheduler')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'scheduler'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="inline-block w-5 h-5 mr-2" />
            Scheduler Settings
          </button>
        </nav>
      </div>

      {/* Email Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Sent Emails</h2>
              <Button onClick={() => loadEmailLogs(getUserId())} size="sm">
                Refresh
              </Button>
            </div>
            
            {/* Filters */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <Label>Email Type</Label>
                <select
                  value={logFilters.email_type}
                  onChange={(e) => setLogFilters({ ...logFilters, email_type: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">All Types</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="reminder">Reminder</option>
                  <option value="manual">Manual</option>
                  <option value="automated">Automated</option>
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={logFilters.status}
                  onChange={(e) => setLogFilters({ ...logFilters, status: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button onClick={() => loadEmailLogs(getUserId())} className="w-full">
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>

          {/* Email List */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {emailLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No email logs found. Emails sent through automation will appear here.
                    </td>
                  </tr>
                ) : (
                  emailLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{formatDate(log.sent_at)}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{log.lead_name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{log.lead_email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{log.subject}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 capitalize">{log.email_type?.replace('_', ' ') || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {log.lead_email && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStopEmailsFromLog(log.lead_email, log.lead_name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Stop all emails to this address"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stopped Emails (Opt-outs) Tab */}
      {activeTab === 'optouts' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-semibold">Stopped Emails</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Emails to these addresses will not be sent. Use this to stop automated follow-ups.
                </p>
              </div>
              <Button onClick={() => setShowOptoutModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Email to Stop List
              </Button>
            </div>
            
            {/* Info Banner */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">How This Works</p>
                  <p>When you add an email to this list, all automated emails (follow-ups, reminders) will be stopped for that address. You can also stop emails directly from the Email Logs tab by clicking the stop button.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Opt-outs List */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stopped On</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {optouts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      <UserX className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p>No stopped emails. All automated emails are being sent normally.</p>
                    </td>
                  </tr>
                ) : (
                  optouts.map((optout) => (
                    <tr key={optout.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{optout.email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{optout.name || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{optout.reason || 'Manual opt-out'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(optout.opted_out_at)}</td>
                      <td className="px-6 py-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveOptout(optout.id, optout.email)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Re-enable emails to this address"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Re-enable
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Opt-out Modal */}
      {showOptoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Stop Emails to Address</h3>
              <p className="text-sm text-gray-600 mt-1">
                Add an email address to stop all automated emails.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="optout-email">Email Address *</Label>
                <Input
                  id="optout-email"
                  type="email"
                  value={newOptoutEmail}
                  onChange={(e) => setNewOptoutEmail(e.target.value)}
                  placeholder="customer@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="optout-name">Name (optional)</Label>
                <Input
                  id="optout-name"
                  type="text"
                  value={newOptoutName}
                  onChange={(e) => setNewOptoutName(e.target.value)}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label htmlFor="optout-reason">Reason (optional)</Label>
                <Input
                  id="optout-reason"
                  type="text"
                  value={newOptoutReason}
                  onChange={(e) => setNewOptoutReason(e.target.value)}
                  placeholder="e.g., Customer requested no emails"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowOptoutModal(false);
                  setNewOptoutEmail('');
                  setNewOptoutName('');
                  setNewOptoutReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddOptout}
                disabled={isLoading || !newOptoutEmail}
                className="bg-red-600 hover:bg-red-700"
              >
                <Ban className="w-4 h-4 mr-2" />
                Stop Emails
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">Email Templates</h2>
              <p className="text-sm text-gray-600 mt-1">Create and manage email templates with dynamic variables</p>
            </div>
            <div className="space-x-2">
              {templates.length === 0 && (
                <Button onClick={initializeTemplates} variant="outline" disabled={isLoading}>
                  <Plus className="w-4 h-4 mr-2" />
                  Initialize Default Templates
                </Button>
              )}
              <Button onClick={() => {
                setEditingTemplate({ name: '', subject: '', body: '', template_type: 'custom', variables: [], is_active: true });
                setShowTemplateModal(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>
          </div>

          {templates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Mail className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
              <p className="text-gray-500 mb-4">Click "Initialize Default Templates" to get started with pre-built templates</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => (
                <div key={template.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">Type: {template.template_type}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTemplate(template);
                          setShowTemplateModal(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteTemplate(template.id);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">Subject:</p>
                    <p className="text-sm text-gray-600">{template.subject}</p>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">Body Preview:</p>
                    <p className="text-sm text-gray-600 line-clamp-3">{template.body}</p>
                  </div>
                  {template.variables && template.variables.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Available Variables:</p>
                      <div className="flex flex-wrap gap-2">
                        {template.variables.map((variable) => (
                          <span key={variable} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                            {`{{${variable}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scheduler Tab */}
      {activeTab === 'scheduler' && schedulerConfig && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-6">Email Automation Settings</h2>
          
          {/* Follow-up Settings */}
          <div className="mb-6 p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Follow-up Emails</h3>
                <p className="text-sm text-gray-600">Sent after a lead requests an estimate</p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={schedulerConfig.follow_up_enabled}
                  onChange={(e) => setSchedulerConfig({ ...schedulerConfig, follow_up_enabled: e.target.checked })}
                  className="mr-2 h-5 w-5"
                />
                <span className="text-sm font-medium">Enabled</span>
              </label>
            </div>
            <div>
              <Label>Delay (hours after lead creation)</Label>
              <Input
                type="number"
                min="1"
                value={schedulerConfig.follow_up_delay_hours}
                onChange={(e) => setSchedulerConfig({ ...schedulerConfig, follow_up_delay_hours: parseInt(e.target.value) })}
                className="max-w-xs"
              />
            </div>
          </div>

          {/* Reminder Settings */}
          <div className="mb-6 p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Measurement Reminders</h3>
                <p className="text-sm text-gray-600">Sent if lead hasn't completed measurement</p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={schedulerConfig.reminder_enabled}
                  onChange={(e) => setSchedulerConfig({ ...schedulerConfig, reminder_enabled: e.target.checked })}
                  className="mr-2 h-5 w-5"
                />
                <span className="text-sm font-medium">Enabled</span>
              </label>
            </div>
            <div>
              <Label>Delay (hours after lead creation)</Label>
              <Input
                type="number"
                min="1"
                value={schedulerConfig.reminder_delay_hours}
                onChange={(e) => setSchedulerConfig({ ...schedulerConfig, reminder_delay_hours: parseInt(e.target.value) })}
                className="max-w-xs"
              />
            </div>
          </div>

          {/* Max Emails */}
          <div className="mb-6 p-4 border rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Rate Limiting</h3>
              <p className="text-sm text-gray-600 mb-4">Maximum emails to send per scheduler run</p>
            </div>
            <div>
              <Label>Max emails per run</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={schedulerConfig.max_emails_per_run}
                onChange={(e) => setSchedulerConfig({ ...schedulerConfig, max_emails_per_run: parseInt(e.target.value) })}
                className="max-w-xs"
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 10-20 to avoid rate limits</p>
            </div>
          </div>

          <Button onClick={handleSaveSchedulerConfig} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateModal && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{editingTemplate.id ? 'Edit Template' : 'New Template'}</h2>
              <button onClick={() => {
                setShowTemplateModal(false);
                setEditingTemplate(null);
              }} className="text-gray-500 hover:text-gray-700 text-2xl">
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={editingTemplate.name || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  placeholder="e.g., Welcome Email"
                />
              </div>

              <div>
                <Label>Template Type</Label>
                <select
                  value={editingTemplate.template_type || 'custom'}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, template_type: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="follow_up">Follow-up</option>
                  <option value="reminder">Reminder</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <Label>Email Subject</Label>
                <Input
                  value={editingTemplate.subject || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  placeholder="Your subject line here"
                />
              </div>

              <div>
                <Label>Email Body</Label>
                <textarea
                  value={editingTemplate.body || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                  rows={12}
                  className="w-full border rounded px-3 py-2 font-mono text-sm"
                  placeholder="Email body with {{variables}}"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available variables: {'{{customer_name}}'}, {'{{company_name}}'}, {'{{contact_email}}'}, {'{{contact_phone}}'}, {'{{estimate_link}}'}
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingTemplate.is_active !== false}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, is_active: e.target.checked })}
                  className="mr-2 h-4 w-4"
                />
                <Label>Active (will be used in automation)</Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={() => handleSaveTemplate(editingTemplate)} disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save Template'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailManagement;